import { Injectable, signal, computed } from '@angular/core';

export interface ChecklistStep {
  id: string;
  label: string;
  description: string;
  route: string;
  icon: string;
}

interface OnboardingState {
  dismissed: boolean;
  collapsed: boolean;
  minimized: boolean;
  hasSeenWelcome: boolean;
  completedSteps: string[];
  completedTours: string[];
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly KEY = 'sw_ob_v2';

  private _state = signal<OnboardingState>(this.load());

  constructor() {
    // Keep onboarding/tour state consistent across browser tabs. Without this, a second
    // tab holds a stale in-memory snapshot and its next save() clobbers the other tab's
    // progress — which manifested as the tour button misbehaving in the latest tab.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== this.KEY) return;
        this._state.set(this.load());
      });
    }
  }

  readonly checklist: ChecklistStep[] = [
    { id: 'view_dashboard', label: 'Explore the dashboard', description: 'See your operation metrics at a glance', route: '/dashboard', icon: 'dashboard' },
    { id: 'add_site', label: 'Add your first site', description: 'Add a physical location to track', route: '/sites', icon: 'sites' },
    { id: 'create_task', label: 'Create a task', description: 'Assign work to your team', route: '/tasks', icon: 'tasks' },
    { id: 'log_incident', label: 'Log an incident', description: 'Report and track safety events', route: '/incidents', icon: 'incidents' },
    { id: 'schedule_audit', label: 'Schedule an audit', description: 'Set up a compliance check', route: '/audits', icon: 'audits' },
    { id: 'invite_user', label: 'Invite a team member', description: 'Bring your team on board', route: '/users', icon: 'users' },
  ];

  readonly completedSteps = computed(() => this._state().completedSteps);
  readonly completedCount = computed(() => this._state().completedSteps.length);
  readonly totalSteps = this.checklist.length;
  readonly progressPct = computed(() => Math.round((this._state().completedSteps.length / this.totalSteps) * 100));
  readonly isDismissed = computed(() => this._state().dismissed);
  readonly isCollapsed = computed(() => this._state().collapsed);
  readonly isMinimized = computed(() => this._state().minimized);
  readonly hasSeenWelcome = computed(() => this._state().hasSeenWelcome);
  readonly isAllComplete = computed(() => this._state().completedSteps.length >= this.totalSteps);

  isStepDone(id: string): boolean {
    return this._state().completedSteps.includes(id);
  }

  completeStep(id: string): void {
    if (this.isStepDone(id)) return;
    this._state.update(s => ({ ...s, completedSteps: [...s.completedSteps, id] }));
    this.save();
  }

  markWelcomeSeen(): void {
    this._state.update(s => ({ ...s, hasSeenWelcome: true }));
    this.save();
  }

  dismiss(): void {
    this._state.update(s => ({ ...s, dismissed: true }));
    this.save();
  }

  toggleCollapse(): void {
    this._state.update(s => ({ ...s, collapsed: !s.collapsed }));
    this.save();
  }

  toggleMinimize(): void {
    this._state.update(s => ({ ...s, minimized: !s.minimized }));
    this.save();
  }

  isTourDone(tourId: string): boolean {
    return this._state().completedTours.includes(tourId);
  }

  completeTour(tourId: string): void {
    if (this.isTourDone(tourId)) return;
    this._state.update(s => ({ ...s, completedTours: [...s.completedTours, tourId] }));
    this.save();
  }

  reset(): void {
    const fresh: OnboardingState = { dismissed: false, collapsed: false, minimized: false, hasSeenWelcome: false, completedSteps: [], completedTours: [] };
    this._state.set(fresh);
    // Write directly (bypass the union-merge in save(), which would resurrect the cleared arrays).
    try { localStorage.setItem(this.KEY, JSON.stringify(fresh)); } catch { /* ignore */ }
  }

  private load(): OnboardingState {
    try {
      const raw = localStorage.getItem(this.KEY);
      if (raw) return { ...this.defaults(), ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return this.defaults();
  }

  private defaults(): OnboardingState {
    return { dismissed: false, collapsed: false, minimized: false, hasSeenWelcome: false, completedSteps: [], completedTours: [] };
  }

  /**
   * Merge the current in-memory state over the freshest snapshot on disk before writing,
   * so a save from one tab never drops progress another tab persisted meanwhile.
   * Array fields (completed steps/tours) are unioned rather than overwritten.
   */
  private save(): void {
    try {
      const onDisk = this.load();
      const mine = this._state();
      const merged: OnboardingState = {
        ...onDisk,
        ...mine,
        completedSteps: Array.from(new Set([...onDisk.completedSteps, ...mine.completedSteps])),
        completedTours: Array.from(new Set([...onDisk.completedTours, ...mine.completedTours])),
      };
      this._state.set(merged);
      localStorage.setItem(this.KEY, JSON.stringify(merged));
    } catch { /* ignore */ }
  }
}
